import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import UploadStep from './components/UploadStep';
import PreviewStep from './components/PreviewStep';
import VerifyStep from './components/VerifyStep';
import ExportStep from './components/ExportStep';
import TrackLibrary from './components/library/TrackLibrary';
import Login from './components/Login';
import ManageUsers from './components/ManageUsers';

const API = process.env.REACT_APP_API_URL || '';

const STEPS = ['upload', 'preview', 'verify', 'export'];
const STEP_LABELS = ['Import', 'Review', 'Verify', 'Export'];

const NAV_ITEMS = [
  { key: 'build', label: 'Build', icon: BuildIcon },
  { key: 'library', label: 'Track Library', icon: LibraryIcon },
  { key: 'users', label: 'Manage Users', icon: UsersIcon }
];

function App() {
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'signed_in' | 'signed_out'
  const [user, setUser] = useState(null);
  const [googleConfigured, setGoogleConfigured] = useState(true);
  const [view, setView] = useState('build'); // 'build' | 'library' | 'users'
  const [step, setStep] = useState('upload');
  const [sessionId, setSessionId] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [editedData, setEditedData] = useState(null);

  const checkAuth = useCallback(() => {
    axios.get(`${API}/api/auth/me`)
      .then(res => {
        setUser(res.data.user);
        setGoogleConfigured(res.data.googleConfigured);
        setAuthState(res.data.user ? 'signed_in' : 'signed_out');
      })
      .catch(() => setAuthState('signed_out'));
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    document.title = authState === 'signed_in' ? 'Build Tool' : 'Sign in';
  }, [authState]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
    setAuthState('signed_out');
  };

  const handleUploadComplete = (id, data) => {
    setSessionId(id);
    setSessionData(data);
    setEditedData({
      project: {
        ...(data.metadata?.project || {}),
        releaseDate: data.metadata?.project?.releaseDate || data.userInputs?.releaseDate || ''
      },
      products: data.metadata?.products || [],
      tracks: data.metadata?.tracks || [],
      contributors: data.metadata?.contributors || [],
      otherContributors: data.metadata?.otherContributors || []
    });
    setStep('preview');
  };

  const handlePreviewComplete = (data) => {
    setEditedData(data);
    setStep('verify');
  };

  const handleVerifyComplete = (data) => {
    setEditedData(data);
    setStep('export');
  };

  const handleReset = () => {
    setStep('upload');
    setSessionId(null);
    setSessionData(null);
    setEditedData(null);
  };

  const currentStepIdx = STEPS.indexOf(step);
  const projectCode = editedData?.project?.projectCode;

  if (authState === 'loading') {
    return <LoadingPage><Spinner /></LoadingPage>;
  }

  if (authState === 'signed_out') {
    const params = new URLSearchParams(window.location.search);
    return <Login error={params.get('auth_error')} googleConfigured={googleConfigured} />;
  }

  return (
    <Layout>
      <SideNav>
        <NavHeader>
          <Wordmark>
            <WordmarkTop>Build Tool</WordmarkTop>
            <WordmarkSub>Pinkfong · Relentless Label</WordmarkSub>
          </Wordmark>
        </NavHeader>

        <NavList>
          {NAV_ITEMS.map(item => (
            <NavItem key={item.key} active={view === item.key} onClick={() => setView(item.key)}>
              <item.icon />
              {item.label}
              {view === item.key && <RecDot />}
            </NavItem>
          ))}
        </NavList>

        {view === 'build' && step !== 'upload' && (
          <SessionPanel>
            <SessionLabel>Session</SessionLabel>
            <SessionCode>{projectCode || '—'}</SessionCode>
            <SessionSteps>
              {STEPS.map((s, i) => (
                <SessionStepRow key={s} active={step === s} done={currentStepIdx > i}>
                  <SessionStepNum>{String(i + 1).padStart(2, '0')}</SessionStepNum>
                  <SessionStepLabel>{STEP_LABELS[i]}</SessionStepLabel>
                  {currentStepIdx > i && <SessionStepCheck><CheckIcon /></SessionStepCheck>}
                  {step === s && <RecDot small />}
                </SessionStepRow>
              ))}
            </SessionSteps>
            <ResetBtn onClick={handleReset}>
              <ResetIcon /> New build
            </ResetBtn>
          </SessionPanel>
        )}

        <NavFooter>
          <UserChip>
            {user?.picture && <UserAvatar src={user.picture} alt="" />}
            <UserInfo>
              <UserName>{user?.name}</UserName>
              <UserEmail>{user?.email}</UserEmail>
            </UserInfo>
            <LogoutBtn onClick={handleLogout} title="Sign out">
              <LogoutIcon />
            </LogoutBtn>
          </UserChip>
          <Reel />
        </NavFooter>
      </SideNav>

      <MainArea>
        <Content>
          {view === 'library' && <TrackLibrary />}
          {view === 'users' && <ManageUsers currentUser={user} />}

          {view === 'build' && step === 'upload' && (
            <UploadStep onComplete={handleUploadComplete} />
          )}
          {view === 'build' && step === 'preview' && sessionData && (
            <PreviewStep
              sessionId={sessionId}
              sessionData={sessionData}
              editedData={editedData}
              setEditedData={setEditedData}
              onComplete={handlePreviewComplete}
            />
          )}
          {view === 'build' && step === 'verify' && (
            <VerifyStep
              sessionId={sessionId}
              sessionData={sessionData}
              editedData={editedData}
              setEditedData={setEditedData}
              onComplete={handleVerifyComplete}
            />
          )}
          {view === 'build' && step === 'export' && (
            <ExportStep
              sessionId={sessionId}
              editedData={editedData}
              sessionData={sessionData}
              onReset={handleReset}
            />
          )}
        </Content>
      </MainArea>
    </Layout>
  );
}

// ── Icons ────────────────────────────────────────────────────────────────

function BuildIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2 8.5L8 3l6 5.5M3.5 7v6h9V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LibraryIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="2.5" width="3" height="11" rx="0.5" stroke="currentColor" strokeWidth="1.4" />
      <rect x="6.75" y="2.5" width="3" height="11" rx="0.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11.2 3.3l2.6 10.1-2.9.75L8.3 4.1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M13 8A5 5 0 1 1 11.5 4.3M13 2v3.5h-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
      <path d="M3 7.5l2.7 2.7L11 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5.2" r="2.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.8 13.5c.5-2.4 2.2-3.8 4.2-3.8s3.7 1.4 4.2 3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M10.5 3.2a2.3 2.3 0 010 4M12.2 9.9c1.6.4 2.7 1.6 3.1 3.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M6.5 2.5H3.8a1 1 0 00-1 1v9a1 1 0 001 1h2.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 5L14 8l-3.5 3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const Layout = styled.div`
  min-height: 100vh;
  display: flex;
`;

const SideNav = styled.nav`
  width: var(--nav-width);
  flex-shrink: 0;
  background: var(--bg-2);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
`;

const NavHeader = styled.div`
  padding: 20px 18px 16px;
  border-bottom: 1px solid var(--border);
`;

const Wordmark = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WordmarkTop = styled.div`
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.2px;
  line-height: 1.1;
`;

const WordmarkSub = styled.div`
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--accent-text);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  line-height: 1.2;
`;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 10px 6px;
`;

const NavItem = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border-radius: var(--radius);
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  background: ${p => p.active ? 'var(--bg-3)' : 'transparent'};
  color: ${p => p.active ? 'var(--text)' : 'var(--text-2)'};
  transition: background 0.12s, color 0.12s;

  svg { flex-shrink: 0; color: ${p => p.active ? 'var(--accent)' : 'var(--text-3)'}; }

  &:hover {
    background: var(--bg-3);
    color: var(--text);
  }
`;

const RecDot = styled.span`
  width: ${p => p.small ? '5px' : '6px'};
  height: ${p => p.small ? '5px' : '6px'};
  border-radius: 50%;
  background: var(--accent);
  margin-left: auto;
  flex-shrink: 0;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const SessionPanel = styled.div`
  margin: 6px 10px 0;
  padding: 14px 12px;
  background: var(--bg-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
`;

const SessionLabel = styled.div`
  font-family: var(--font-mono);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: var(--text-3);
  margin-bottom: 3px;
`;

const SessionCode = styled.div`
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 12px;
`;

const SessionSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 12px;
`;

const SessionStepRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 2px;
  opacity: ${p => (p.active || p.done) ? 1 : 0.5};
`;

const SessionStepNum = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--text-3);
`;

const SessionStepLabel = styled.span`
  font-size: 12px;
  font-weight: ${p => p.active ? '600' : '500'};
  color: var(--text);
  flex: 1;
`;

const SessionStepCheck = styled.span`
  color: var(--green);
  display: flex;
`;

const ResetBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--bg-2);
  border: 1px solid var(--border);
  color: var(--text-2);
  font-size: 11.5px;
  font-weight: 500;
  padding: 7px 12px;
  border-radius: var(--radius);
  transition: all 0.12s;
  &:hover {
    background: var(--bg-4);
    color: var(--text);
    border-color: var(--border-2);
  }
`;

const NavFooter = styled.div`
  margin-top: auto;
  padding: 12px 18px 16px;
`;

const UserChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  margin-bottom: 10px;
`;

const UserAvatar = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const UserName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 10.5px;
  color: var(--text-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LogoutBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-3);
  flex-shrink: 0;
  transition: all 0.12s;
  &:hover { background: var(--bg-3); color: var(--text); }
`;

// A quiet reference to tape/reel imagery — a thin dashed line, the way a
// run sheet's footer might mark the leader tape. Structural, not literal.
const Reel = styled.div`
  height: 1px;
  background: repeating-linear-gradient(
    90deg,
    var(--border-2) 0, var(--border-2) 3px,
    transparent 3px, transparent 7px
  );
`;

const LoadingPage = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2.5px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: app-spin 0.7s linear infinite;
  @keyframes app-spin { to { transform: rotate(360deg); } }
`;

const MainArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Content = styled.main`
  flex: 1;
  overflow: auto;
`;

export default App;
